/**
 * Helper centralizado de timezone para el portal (Sprint 18 P27).
 *
 * DOCTRINA del proyecto (cerrada 2026-05-25, espejo del backend
 * `app/utils/tiempo.py`):
 *
 * - **Almacenar UTC, mostrar Santiago.** Lo que viene del backend en
 *   `creado_at` / `actualizado_at` es ISO 8601 con offset Z (UTC); el
 *   browser ya lo interpreta correctamente con `new Date()`. Pero
 *   cuando NECESITAMOS "hoy" como día calendario chileno, NO usar
 *   `new Date().toISOString().slice(0, 10)` porque devuelve la fecha
 *   UTC (off-by-one entre 20:00-23:59 hora Chile, hora invierno).
 *
 * - **Prohibido fuera de este módulo:** `toISOString().slice(0, 10)`
 *   para "hoy" y construcciones equivalentes. Convención #16 backend
 *   extendida a frontend.
 *
 * - **APP_TZ = America/Santiago** fijo. Si el dev abre la UI desde
 *   otro huso, igual ve fechas Santiago coherentes con lo que ve el
 *   barrio en Chile.
 *
 * USO:
 *
 *     import { hoy, ahora, formatearFecha } from '@/lib/tiempo';
 *
 *     const fechaInputDefault = hoy();              // "2026-05-29"
 *     const timestampLegible = formatearFechaHora(  // "2026-05-29 14:30"
 *       row.creado_at
 *     );
 */

const APP_TZ = 'America/Santiago';


/**
 * Día calendario Santiago como ISO `yyyy-mm-dd`. Reemplazo de
 * `new Date().toISOString().slice(0, 10)` que devuelve UTC.
 */
export function hoy(): string {
  // Formato es-CL: "29-05-2026" → reordenar a ISO.
  // Más simple: usar 'sv-SE' que ya devuelve ISO.
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: APP_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}


/**
 * Datetime ISO Santiago como `yyyy-mm-dd HH:MM`.
 */
export function ahora(): string {
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: APP_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
    .format(new Date())
    .replace(' ', ' '); // sv-SE ya devuelve "yyyy-mm-dd HH:MM"
}


/**
 * Formatea una fecha (Date | string ISO | null) como `yyyy-mm-dd`
 * Santiago. Si recibe string, lo parsea primero.
 */
export function formatearFecha(input: Date | string | null | undefined): string {
  if (!input) return '';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: APP_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}


const MESES_CORTOS = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];

/**
 * Formatea una fecha CALENDARIO (`AAAA-MM-DD`) como `27 ago`.
 *
 * Parsea el string a mano en vez de usar `new Date()`: una fecha sin hora se
 * interpreta como medianoche UTC, y al formatearla en Santiago (UTC-4) sale
 * el día ANTERIOR. Un saldo del 27 de agosto se mostraría como del 26.
 *
 * Para timestamps con hora, usar `formatearFecha` / `formatearFechaHora`, que
 * sí deben convertir de zona.
 */
export function fechaCorta(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  const mes = MESES_CORTOS[parseInt(m[2], 10) - 1];
  if (!mes) return '';
  return `${parseInt(m[3], 10)} ${mes}`;
}


/**
 * Formatea una fecha como `HH:MM` Santiago.
 */
export function formatearHora(input: Date | string | null | undefined): string {
  if (!input) return '';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: APP_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}


/**
 * Formatea una fecha como `yyyy-mm-dd HH:MM` Santiago.
 */
export function formatearFechaHora(input: Date | string | null | undefined): string {
  if (!input) return '';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(d.getTime())) return '';
  return new Intl.DateTimeFormat('sv-SE', {
    timeZone: APP_TZ,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}


/**
 * Formatea un último acceso en lenguaje corto: `hoy, HH:MM`,
 * `ayer, HH:MM` o `dd-mm-aaaa HH:MM`, siempre en hora de Santiago.
 *
 * Vivía inline en `VecinoHome` (donde el vecino ve su propio acceso).
 * Se extrajo acá al mostrarlo también en la ficha de Comisión: el mismo
 * dato (`auth.users.last_sign_in_at`) leído desde los dos lados debe
 * leerse igual en pantalla, y una segunda copia del formateador se iba a
 * desincronizar sola.
 */
export function formatearUltimoAcceso(
  input: Date | string | null | undefined,
): string {
  if (!input) return '';
  const d = typeof input === 'string' ? new Date(input) : input;
  if (isNaN(d.getTime())) return '';

  // Comparar por día calendario de Santiago, no del navegador: si no,
  // un operador en otra zona ve "ayer" lo que para el barrio es "hoy".
  const diaDe = (x: Date) => formatearFecha(x);
  const ahoraDate = new Date();
  const ayerDate = new Date(ahoraDate.getTime() - 24 * 60 * 60 * 1000);
  const hora = formatearHora(d);

  if (diaDe(d) === diaDe(ahoraDate)) return `hoy, ${hora}`;
  if (diaDe(d) === diaDe(ayerDate)) return `ayer, ${hora}`;
  return `${isoAFechaDisplay(formatearFecha(d))} ${hora}`;
}

// === Entrada de fecha dd-mm-aaaa (P27, ensayo LIVE 2026-06) ===========
//
// El <input type="date"> nativo guarda ISO yyyy-mm-dd, pero renderiza el
// picker según el locale del NAVEGADOR: en un browser en-US, tipear
// "01.06.2026" (1-jun) se interpreta mes-primero → 2026-01-06 (6-ene).
// Bug real detectado en el ensayo LIVE. La doctrina del proyecto es
// dd-mm-aaaa, NO estilo USA. `InputFecha` usa estos helpers para una
// entrada locale-independiente: el usuario teclea dd-mm-aaaa y se
// almacena/emite ISO yyyy-mm-dd, sin depender del navegador.

/** Máscara en vivo dd-mm-aaaa: deja solo dígitos (máx 8) e inserta guiones. */
export function formatearFechaEnVivo(raw: string): string {
  const d = raw.replace(/\D/g, '').slice(0, 8);
  const dd = d.slice(0, 2);
  const mm = d.slice(2, 4);
  const aaaa = d.slice(4, 8);
  let out = dd;
  if (d.length >= 3) out += `-${mm}`;
  if (d.length >= 5) out += `-${aaaa}`;
  return out;
}

/**
 * dd-mm-aaaa → ISO yyyy-mm-dd SOLO si es una fecha calendario real y
 * completa (rechaza 31-02-2026, 00-00-0000, etc.). Si no, retorna ''.
 */
export function displayFechaAIso(display: string): string {
  const m = /^(\d{2})-(\d{2})-(\d{4})$/.exec(display.trim());
  if (!m) return '';
  const [, dd, mm, aaaa] = m;
  const day = Number(dd);
  const month = Number(mm);
  const year = Number(aaaa);
  // Piso/techo de sanidad: ninguna fecha del sistema (nacimientos de
  // personas vivas, tenencias, pagos, vencimientos, proyectos) cae fuera
  // de 1900-2100. Atrapa typos de año tipo "0206" → 206 d.C.
  if (year < 1900 || year > 2100) return '';
  const iso = `${aaaa}-${mm}-${dd}`;
  const dt = new Date(`${iso}T00:00:00`);
  if (
    Number.isNaN(dt.getTime()) ||
    dt.getDate() !== day ||
    dt.getMonth() + 1 !== month ||
    dt.getFullYear() !== year
  ) {
    return '';
  }
  return iso;
}

/** ISO yyyy-mm-dd (o ISO con tiempo) → dd-mm-aaaa para mostrar; '' si vacío. */
export function isoAFechaDisplay(iso: string | null | undefined): string {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  if (!m) return '';
  const [, aaaa, mm, dd] = m;
  return `${dd}-${mm}-${aaaa}`;
}
